import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Filter } from "lucide-react";

const SORTS = [
  { v: "popular", l: "Most loved" },
  { v: "rating", l: "Highest rated" },
  { v: "price_asc", l: "Price: low to high" },
  { v: "price_desc", l: "Price: high to low" },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const sort = searchParams.get("sort") || "popular";
  const search = searchParams.get("q") || "";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (sort) params.set("sort", sort);
      if (search) params.set("search", search);
      try {
        const [pRes, cRes] = await Promise.all([api.get(`/products?${params}`), api.get(`/categories`)]);
        setProducts(pRes.data);
        setCategories(cRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, sort, search]);

  const update = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set(k, v); else next.delete(k);
    setSearchParams(next);
  };

  return (
    <div data-testid="shop-page" className="welljio-mesh">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-12">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-welljio-subtle mb-4">
            <span className="w-6 h-px bg-welljio-subtle/60" />
            All products
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05]">Shop wellness essentials.</h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          <aside className="lg:w-64 shrink-0 space-y-8 welljio-glass-card rounded-3xl p-6 h-fit lg:sticky lg:top-28">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-welljio-subtle mb-3">Categories</p>
              <div className="space-y-1">
                <button
                  data-testid="filter-all-categories"
                  onClick={() => update("category", "")}
                  className={`w-full text-left px-4 py-2.5 rounded-full text-sm transition-colors ${
                    !category ? "bg-welljio-ink text-white" : "hover:bg-welljio-tint text-welljio-ink"
                  }`}
                >
                  All
                </button>
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    data-testid={`filter-category-${c.slug}`}
                    onClick={() => update("category", c.slug)}
                    className={`w-full text-left px-4 py-2.5 rounded-full text-sm transition-colors ${
                      category === c.slug ? "bg-welljio-ink text-white" : "hover:bg-welljio-tint text-welljio-ink"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-welljio-subtle mb-3">Sort by</p>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => update("sort", e.target.value)}
                className="w-full rounded-full border border-slate-200 px-5 py-2.5 text-sm bg-white focus:border-welljio-accent outline-none"
              >
                {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
          </aside>

          <div className="flex-1">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_,i)=>(
                  <div key={i} className="aspect-[3/4] welljio-glass-card rounded-3xl animate-pulse-soft" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <p className="text-welljio-muted">No products found.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((p, i) => <ProductCard product={p} key={p.id} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
